/* eslint-disable no-nested-ternary */
/* eslint-disable no-param-reassign */
import DownloadModal from '@brands/ChatWindow/ChatModal/DownloadModal';
import { Loading } from '@brands/Components/LoadingSpinner/Loading';
import FileUploadProgressBar from '@brands/Components/UploadProgressBar/FileUploadProgressBar';
import { useAppSelector } from '@brands/hooks/useReduxHook';
import useRoleNavigate from '@brands/hooks/useRoleNavigate';
import { getUserAvatar } from '@brands/services/identity/getUserAvatar';
import { selectAuth } from '@brands/store/selectors/auth';
import { selectShowVideoThumbnail } from '@brands/store/selectors/showVideoThumbnail';
import { requestStatisticsRefresh } from '@brands/store/slices/conversationStatisticsSlice';
import {
  hasUnreadMessageInPatientCA,
  hasUnreadMessageInPatientProvider,
  hasUnreadMessageInPrivate,
} from '@brands/Utils/getUnreadMessageNotification';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import { Button } from '@mui/material';
import { Conversation } from '@twilio/conversations';
import throttle from 'lodash/throttle';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDetectClickOutside } from 'react-detect-click-outside';
import { BsThreeDotsVertical } from 'react-icons/bs';
import { IoIosArrowDroprightCircle, IoIosSearch } from 'react-icons/io';
import { IoDownloadOutline } from 'react-icons/io5';
import { MdKeyboardArrowLeft, MdOutlinePersonAddAlt } from 'react-icons/md';
import { useDispatch, useSelector } from 'react-redux';

import CareAdvisorProfileCardModal from '../../Dashboard/ProfileCards/CareAdvisor/ProfileCards';
import PatientProfileCardModal from '../../Dashboard/ProfileCards/Patient/ProfileCards';
import ProviderProfileCardModal from '../../Dashboard/ProfileCards/Provider/ProfileCards';
import { useConversationMessages, useIdentityContext } from '../../hooks';
import { ICase } from '../../services/cases/types/ICase';
import { markLastReadMessage } from '../../services/communication/markLastReadMessage';
import { IGMConversation } from '../../services/communication/types/IGMConversation';
import { IGMConversationParticipant } from '../../services/communication/types/IGMConversationParticipant';
import { UserProfile, UserRoleName } from '../../services/identity/types/UserProfile';
import AddParticipantModal from '../ChatModal/AddParticipantModal';
import CaseActiveChatBubble from '../ChatModal/ChatBubble/PatientCaseActiveChatBubble';
import LeaveModal from '../ChatModal/LeaveModal';
import styles from './case-styles.module.scss';
import CaseChatFooter from './CaseChatFooter';
import CaseMobileOptionsModal from './CaseMobileOptionsModal';
import { getFriendlyRoleName } from '../../Utils/getFriendlyName';
import { shouldShowDoctorTitle } from '../../Utils/getProviderTitle';
import CollectorProfileCardModal from '@brands/Dashboard/ProfileCards/Collector/ProfileCards';

interface MessageBodyProps {
  conversation: IGMConversation | null;
  twilioConversation: Conversation | null;
  readOnlyConversation: IGMConversation | null;
  isMobile: boolean;
  isProfileCard?: boolean;
  isVideoConversation?: boolean;
  setLeaveModalOpen: (isOpen: boolean) => void;
  leaveModalOpen: boolean;
  conversationSid: string | null;
  setConversationSid: (sid: string | null) => void;
  currentCase: ICase | undefined;
  updateUnreadNotification: () => void;
  conversationType: string | undefined;
  isArchivedConversation?: boolean;
  patientCurrentState: string | undefined;
  loading: boolean;
  setLoading: React.Dispatch<React.SetStateAction<boolean>>;
  conversations: {
    conversationType: string;
    conversation?: IGMConversation | undefined;
    createNewConversation?: boolean | undefined;
    conversationCase?: ICase | undefined;
  }[];
}

const CaseMessageBody: React.FC<MessageBodyProps> = ({
  conversation,
  twilioConversation,
  readOnlyConversation,
  isMobile,
  isProfileCard,
  isVideoConversation,
  leaveModalOpen,
  setLeaveModalOpen,
  conversationSid,
  setConversationSid,
  currentCase,
  updateUnreadNotification,
  conversationType,
  isArchivedConversation,
  patientCurrentState,
  loading,
  setLoading,
  conversations,
}) => {
  const contextValues = useIdentityContext();
  const roleNavigate = useRoleNavigate();
  const dispatch = useDispatch();
  const { userInfo } = useAppSelector(selectAuth);
  const identity = contextValues.userInfo?.id.toString();
  // The "patient" participant slot on a case conversation can be keyed to
  // either the dependent's own id or their guardian's id depending on how the
  // conversation was created (see CaseMessageInfoCreateNewConversation, which
  // uses guardian_id for a Dependent patient). Whichever one of the pair is
  // logged in, accept a match on either id so neither side goes read-only.
  const myEffectiveIdentities =
    currentCase?.patient?.guardian_id != null &&
    (Number(currentCase.patient.guardian_id) === contextValues.userInfo?.id ||
      Number(currentCase.patient.id) === contextValues.userInfo?.id)
      ? [Number(currentCase.patient.id), Number(currentCase.patient.guardian_id)]
      : [contextValues.userInfo?.id];
  const [search, setSearch] = useState<string>('');
  const [query, setQuery] = useState<string>('');
  const [hoveredMessageId, setHoveredMessageId] = useState<string | null>(null);
  const [profileCardModal, setProfileCardModal] = useState(false);
  const [providerProfileCardModal, setProviderProfileCardModal] = useState(false);
  const [collectorProfileCardModal, setCollectorProfileCardModal] = useState(false);
  const [caProfileCardModal, setCAProfileCardModal] = useState(false);
  const [participant, setParticipant] = useState<UserProfile | null>(null);
  const [isMobileOptionsModalOpen, setIsMobileOptionsModalOpen] = useState(false);
  const messagesParams = useMemo(() => (search ? { search } : {}), [search]);
  const [progress, setProgress] = useState(0);
  const [displayProgressBar, setDisplayProgressBar] = useState(false);
  const [choosenFiles, setChoosenFiles] = useState<File[]>([]);
  const [editMode, setEditMode] = useState(false);
  const [isInEditMode, setIsInEditMode] = useState<string>('');
  const showVideoThumbnail = useSelector(selectShowVideoThumbnail);
  const [remoteParticipant, setRemoteParticipant] = useState<UserProfile[]>();
  const isUserParticipant = conversation?.participants?.some((p) => p.identity === userInfo.id);
  const { messages, editMessage, deleteMessage, sendMessage, loadNextPage } = useConversationMessages(
    twilioConversation,
    messagesParams,
    readOnlyConversation,
  );
  const [showDropDown, setShowDropDown] = useState<boolean>(false);
  const [addParticipantModalOpen, setAddParticipantModalOpen] = useState(false);
  const [downloadModalOpen, setDownloadModalOpen] = useState(false);
  const ref = useDetectClickOutside({ onTriggered: () => setShowDropDown(false) });

  const userRole = useMemo(() => {
    if (!contextValues.userInfo) {
      return null;
    }

    return contextValues.userInfo.role;
  }, [contextValues]);

  const listInnerRef = useRef<HTMLDivElement>(null);

  const throttledSearch = throttle(setSearch, 100, { trailing: true });

  const [loadedNextPage, setLoadedNextPage] = useState(false);

  const getRemoteParticipants = (): IGMConversationParticipant[] | undefined => {
    let remoteParticipants: any[] | undefined = [];

    const participants: UserProfile[] = [];
    const participantsId: number[] = [];

    if (currentCase?.patient) {
      if (currentCase?.patient.guardian_relationship) {
        participantsId.push(currentCase.patient?.guardian_id || 0);
      } else {
        participantsId.push(currentCase.patient.id);
      }
      participants.push(currentCase.patient);
    }
    if (currentCase?.care_advisor) {
      participantsId.push(currentCase.care_advisor.id);
      participants.push(currentCase.care_advisor);
    }
    if (currentCase?.provider) {
      participantsId.push(currentCase.provider.id);
      participants.push(currentCase.provider);
    }

    if (conversation?.participants) {
      remoteParticipants = conversation?.participants?.filter((p) => +p.identity !== contextValues.userInfo?.id);
    }

    remoteParticipants = remoteParticipants?.filter((object1) =>
      participants.some((object2) => +object1.identity === object2.id),
    );

    if (
      (conversationType === `${UserRoleName.Patient}_${UserRoleName.CareAdvisor}` ||
        conversationType === `${UserRoleName.Patient}_${UserRoleName.Provider}`) &&
      currentCase?.patient.guardian_relationship
    ) {
      remoteParticipants.push(
        conversation?.participants?.find(
          (conversationParticipant) => conversationParticipant.role === UserRoleName.Patient,
        ),
      );
    }

    return remoteParticipants;
  };

  const getRemoteParticipantsForthisConversation = (): UserProfile[] | undefined => {
    let remoteParticipants: IGMConversationParticipant[];
    let participants: UserProfile[] = [];
    if (currentCase) {
      if (currentCase?.patient) {
        participants.push(currentCase.patient);
      }
      if (currentCase?.care_advisor) {
        participants.push(currentCase.care_advisor);
      }
      if (currentCase?.provider) {
        participants.push(currentCase.provider);
      }
      if (currentCase?.collector) {
        participants.push(currentCase.collector);
      }
    } else {
      const selectedCase = conversations?.find(
        (activeConversation) => activeConversation.conversation?.sid === conversation?.sid,
      )?.conversationCase;
      if (selectedCase) {
        if (selectedCase?.patient) {
          participants.push(selectedCase.patient);
        }
        if (selectedCase?.care_advisor) {
          participants.push(selectedCase.care_advisor);
        }
        if (selectedCase?.provider) {
          participants.push(selectedCase.provider);
        }
      }
    }

    if (conversation?.participants) {
      participants = participants.filter((p) => p.id !== userInfo?.id);
    }

    if (conversation?.participants) {
      remoteParticipants = conversation.participants.filter((p) => p.identity !== userInfo?.id);
      participants = participants?.filter((object1) =>
        remoteParticipants.some((object2) =>
          object1.guardian_id ? +object1.guardian_id === +object2.identity : +object1.id === +object2.identity,
        ),
      );
    }

    return participants;
  };

  const getParticipantName = (id: string): string => {
    return (remoteParticipant && remoteParticipant.find((p) => p.id === Number(id))?.full_name) || id;
  };

  const onScroll = useCallback(() => {
    if (!listInnerRef.current) {
      return;
    }
    const { scrollTop } = listInnerRef.current;
    if (scrollTop === 0) {
      setLoadedNextPage(true);
      loadNextPage();
    }
  }, [loadNextPage]);

  useEffect(() => {
    if (isMobile) {
      setEditMode(isInEditMode !== '');
    }
  }, [isInEditMode, setEditMode]);

  const handleNavigateBack = (): void => {
    const currentPath = window.location.pathname;
    const segments = currentPath.split('/');
    const newPath = segments.slice(0, segments.length - 1).join('/');
    roleNavigate(newPath);
    setConversationSid(null);
  };

  const addParticipant = (): void => {
    setAddParticipantModalOpen(true);
  };

  const scrollDown = (): void => {
    if (listInnerRef.current) {
      const lastLi = listInnerRef.current.lastElementChild;
      if (lastLi) {
        const { scrollHeight } = listInnerRef.current;
        const { offsetHeight } = listInnerRef.current;
        const lastLiHeight = lastLi.clientHeight;
        listInnerRef.current.scrollTop = scrollHeight - offsetHeight + lastLiHeight;
      }
    }
  };

  const scrollTothis = (number: number, index: number): void => {
    if (index === messages.length - 1 && listInnerRef.current) {
      listInnerRef.current.scrollTop = number;
    }
  };

  const handleMediaLoad = (index: number): void => {
    if ((index === messages.length - 1 || index >= messages.length - 10) && messages[index].media_ids.length > 0) {
      scrollDown();
    }
  };
  useEffect(() => {
    if (!loadedNextPage) {
      scrollDown();
    }
    setLoadedNextPage(false);
  }, [messages, listInnerRef]);

  useEffect(() => {
    if (conversation) {
      const remoteParticipants = getRemoteParticipantsForthisConversation();
      setRemoteParticipant(remoteParticipants);
    }
  }, [conversation, currentCase]);

  useEffect(() => {
    scrollDown();
  }, []);
  const markAsRead = async (): Promise<void> => {
    if (conversationSid && messages.length > 0) {
      const lastMessage = messages[messages.length - 1];
      const checkParticipantLastReadMessage = conversation?.participants?.find(
        (participantSingle) => Number(participantSingle.identity) === Number(contextValues.userInfo?.id),
      );
      const lastReadMessageIndex = checkParticipantLastReadMessage?.last_read_message_index;
      if (
        (lastReadMessageIndex === undefined ||
          lastReadMessageIndex === null ||
          lastReadMessageIndex < lastMessage.index) &&
        (conversation?.state === 'active' || conversation?.state === 'inactive') &&
        lastMessage &&
        lastMessage.index !== lastReadMessageIndex &&
        Number(lastMessage.created_by) !== Number(identity) &&
        userRole?.name !== UserRoleName.Admin &&
        userRole?.name !== UserRoleName.SuperAdmin &&
        userRole?.name !== UserRoleName.OrganizationAdmin
      ) {
        try {
          await markLastReadMessage(conversationSid, lastMessage.sid, userRole?.name);
          updateUnreadNotification();
          dispatch(requestStatisticsRefresh());
        } catch (error) {
          console.error('Failed to mark as read:', error);
        }
      }
    }
  };

  useEffect(() => {
    void markAsRead();
  }, [messages[messages.length - 1], conversationSid]);

  const displayParticipantProfile = (
    e: React.MouseEvent<HTMLDivElement | HTMLSpanElement, MouseEvent>,
    roleName: string,
  ): void => {
    e.stopPropagation();
    e.preventDefault();
    if (currentCase) {
      if (roleName === UserRoleName.Patient || roleName === UserRoleName.Dependent) {
        // DO-3972 / parent DO-3237: when the logged-in user is a Provider
        // and they click a Patient/Dependent in the chat header, route to
        // their Cases dashboard with the patient name pre-filled in the
        // Patient column filter, so they see the patient's full case
        // history (all statuses), not just completed appointments. Other
        // role pairings keep the modal preview behavior.
        //
        // The new-design Cases page hydrates filter state from the URL
        // query string (`parseFiltersFromQuery` over `location.search`),
        // so we just append `?patient_full_name=...` and let the page
        // pick it up. The legacy `/Provider/appointments` route only
        // shows completed cases — that's what the reopen comment was
        // calling out.
        if (userInfo.role.name === UserRoleName.Provider) {
          const targetPatient = currentCase.patient;
          if (targetPatient?.full_name) {
            const search = new URLSearchParams({
              patient_full_name: targetPatient.full_name,
            }).toString();
            roleNavigate(`/dashboard?${search}`);
            return;
          }
        }
        setParticipant(currentCase?.patient || null);
        setProfileCardModal(true);
      } else if (roleName === UserRoleName.Provider) {
        setParticipant(currentCase?.provider || null);
        setProviderProfileCardModal(true);
      } else if (roleName === UserRoleName.CareAdvisor) {
        setParticipant(currentCase?.care_advisor || null);
        setCAProfileCardModal(true);
      } else if (roleName === UserRoleName.Collector) {
        setParticipant(currentCase?.collector || null);
        setCollectorProfileCardModal(true);
      }
    }
  };

  const showUnreadIndicator = (participant: UserProfile): boolean => {
    if (!currentCase) return false;

    const isPatientLike =
      participant.role.name === UserRoleName.Patient || participant.role.name === UserRoleName.Dependent;

    let participantId: number | undefined;

    if (isPatientLike) {
      participantId =
        currentCase.patient.guardian_relationship === null ? currentCase.patient_id : currentCase.patient.guardian_id!;
    } else if (participant.role.name === UserRoleName.Provider) {
      participantId = currentCase.provider_id;
    } else if (participant.role.name === UserRoleName.CareAdvisor) {
      participantId = currentCase.care_advisor_id;
    } else if (participant.role.name === UserRoleName.Collector) {
      if (!currentCase.collector_id) return false;
      participantId = currentCase.collector_id;
    } else {
      return false;
    }

    if (conversationType === `${UserRoleName.Patient}_${UserRoleName.Provider}`) {
      return hasUnreadMessageInPatientProvider(currentCase, participantId);
    }
    if (conversationType === `${UserRoleName.Patient}_${UserRoleName.CareAdvisor}`) {
      return hasUnreadMessageInPatientCA(currentCase, participantId);
    }
    if (conversationType === `${UserRoleName.Provider}_${UserRoleName.CareAdvisor}`) {
      return hasUnreadMessageInPrivate(currentCase, participantId);
    }

    return false;
  };

  return (
    <>
      <div
        className={styles.chatBody}
        style={{
          height: isVideoConversation ? 'calc(100% - 50px)' : '',
          width: isVideoConversation || isProfileCard ? '100%' : '',
          margin: isVideoConversation ? '0' : '',
          display: conversationSid || conversation?.sid ? 'flex' : 'none',
        }}
      >
        <div
          className={`${styles.messageSearch} ${showVideoThumbnail && styles.addMarginTop}`}
          style={{ display: isProfileCard ? 'none' : 'flex' }}
        >
          {isMobile && (
            <Button
              type="button"
              variant="text"
              className={styles.backButton}
              sx={{
                width: 'fit-content',
              }}
              onClick={() => {
                handleNavigateBack();
              }}
            >
              <MdKeyboardArrowLeft />
              Back
            </Button>
          )}
          <div className={styles.wrapDiv}>
            {remoteParticipant?.map((remoteParticipantSingle) => (
              <div
                className={`${styles.participantDivInfo} ${
                  remoteParticipantSingle.status.toLowerCase() !== 'active' ? styles.inactiveParticipant : ''
                }`}
                onClick={(e) => displayParticipantProfile(e, remoteParticipantSingle.role.name)}
              >
                <span className={`${styles.profileImageDate} ${styles.imageContainer}`}>
                  {showUnreadIndicator(remoteParticipantSingle) && <span className={styles.unreasMessage} />}
                  <img
                    alt="Avatar"
                    onError={(event: any) => {
                      event.target.src =
                        'https://media.istockphoto.com/id/1130884625/tr/vekt%C3%B6r/kullan%C4%B1c%C4%B1-%C3%BCyesi-vekt%C3%B6r-simgesi-ui-kullan%C4%B1c%C4%B1-arabirimi-veya-profil-face-avatar-uygulamas%C4%B1.jpg?s=612x612&w=0&k=20&c=jAf5nq1ebnZo8TJbjaB9dMHMvgff7uOk67NkF5CpgB0=';
                      event.target.onError = null;
                    }}
                    className={`fs.exclude ${styles.profileImage}`}
                    src={getUserAvatar(
                      Number(
                        remoteParticipantSingle.role.name !== UserRoleName.Dependent
                          ? remoteParticipantSingle?.id
                          : remoteParticipantSingle.guardian_id,
                      ),
                    )}
                  />
                </span>
                <div className={`${styles.details} ${isMobile && styles.mobileCaseMessageInfo}`}>
                  <div className={styles.detailsNameImage}>
                    <span className={`${styles.participantName} fs.exclude ${!false ? styles.primaryColor : ''}`}>
                      {remoteParticipantSingle.role.name === 'Provider' &&
                        shouldShowDoctorTitle(remoteParticipantSingle.provider_profile?.credentials) && (
                          <span style={{ paddingRight: '4px' }}>Dr.</span>
                        )}
                      {remoteParticipantSingle.role.name !== UserRoleName.Dependent
                        ? remoteParticipantSingle.full_name
                        : remoteParticipantSingle.guardian.full_name}
                    </span>
                  </div>

                  {isUserParticipant ? (
                    <span className={`fs-unmask ${styles.conversationType}`}>
                      {getFriendlyRoleName(remoteParticipantSingle.role.name)}
                    </span>
                  ) : (
                    <span className={`fs-unmask ${styles.conversationType}`}>
                      Read Only
                      {isMobile && (
                        <div
                          className={styles.detailsIcon}
                          style={{ display: false ? 'none' : 'block' }}
                          onClick={() => {
                            roleNavigate(`/case-chat/${currentCase?.id}/${conversation?.sid}`);
                          }}
                        >
                          <IoIosArrowDroprightCircle />
                        </div>
                      )}
                    </span>
                  )}
                  <div className={styles.relation}>
                    {remoteParticipantSingle.role.name === UserRoleName.Dependent && (
                      <span className="fs.exclude">
                        {`${remoteParticipantSingle.guardian_relationship} of `}
                        <span className={styles.relationSpan}>{remoteParticipantSingle.full_name}</span>
                      </span>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
          <div className={styles.searchBoxDiv}>
            <div className={styles.searchBox}>
              <input
                type="text"
                className={`fs.exclude ${styles.inputSearch}`}
                placeholder="Search in this chat"
                onChange={(e) => {
                  setQuery(e.target.value);
                  throttledSearch(e.target.value);
                }}
                value={query}
              />
              <button aria-label="Search" type="button" className={styles.btnSearch} onClick={() => setSearch(query)}>
                <IoIosSearch className={styles.searchIcon} />
              </button>
            </div>
            <div ref={ref} style={{ position: 'relative' }} className={styles.bsThreeDotsVertical}>
              <BsThreeDotsVertical
                id="options"
                className={styles.options}
                onClick={(): void => setShowDropDown(!showDropDown)}
              />
              {showDropDown && (
                <div className={styles.dropDown}>
                  {false &&
                    (userRole?.name === UserRoleName.Provider || userRole?.name === UserRoleName.CareAdvisor) && (
                      <div onClick={() => addParticipant()} className={styles.borderBottom}>
                        Add Participant
                        <MdOutlinePersonAddAlt className={styles.dropDownIcon} />
                      </div>
                    )}
                  {/* Patients reach this chat too. A case the staff status override completed has no report for
                      them; staff case routes don't return report_available, so they keep the old check. */}
                  {currentCase?.status === 'completed' &&
                    currentCase?.one_report?.id &&
                    (userRole?.name !== UserRoleName.Patient || currentCase?.report_available) && (
                      <div onClick={() => roleNavigate(`/oral-health-report/${currentCase?.id}`)}>
                        View Report
                        <DescriptionOutlinedIcon className={styles.dropDownIcon} />
                      </div>
                    )}
                  <div onClick={() => setDownloadModalOpen(true)}>
                    Download Chat
                    <IoDownloadOutline className={styles.dropDownIcon} />
                  </div>
                </div>
              )}
            </div>
          </div>
          {leaveModalOpen && <LeaveModal setOpenModal={setLeaveModalOpen} conversationSid={conversation?.sid} />}
          {downloadModalOpen && (
            <DownloadModal
              setOpenModal={setDownloadModalOpen}
              conversationSid={conversation?.sid}
              setLoading={setLoading}
            />
          )}
        </div>
        {loading ? (
          <Loading dotted />
        ) : (
          <>
            {displayProgressBar && (
              <div className={styles.progressBar}>
                {displayProgressBar && <FileUploadProgressBar progress={progress} />}
              </div>
            )}
            <div
              className={`${styles.popupMessageContainer} w-100
              ${showVideoThumbnail ? styles.adjustHeight : styles.keepCurrentHeight}
              ${isMobile && editMode && styles.forceEditPopupMessageContainer}
              ${
                (userInfo.role.name === UserRoleName.CareAdvisor || userInfo.role.name === UserRoleName.Provider) &&
                conversationType &&
                (conversationType === `${UserRoleName.Patient}_${UserRoleName.CareAdvisor}` ||
                  conversationType === `${UserRoleName.Patient}_${UserRoleName.Provider}`) &&
                styles.mobileExtraAdjust
              }`}
              onScroll={onScroll}
              ref={listInnerRef}
            >
              <ul className="h-100">
                {messages
                  .sort((a, b) => a.index - b.index)
                  .map((message, index) => (
                    <CaseActiveChatBubble
                      messageCreator={
                        getRemoteParticipants()?.find(
                          (activeParticipant) => Number(activeParticipant?.identity) === Number(message.created_by),
                        )?.role === UserRoleName.Patient
                          ? currentCase?.patient
                          : getRemoteParticipants()?.find(
                              (activeParticipant) => Number(activeParticipant?.identity) === Number(message.created_by),
                            )?.role === UserRoleName.Provider
                          ? currentCase?.provider
                          : getRemoteParticipants()?.find(
                              (activeParticipant) => Number(activeParticipant?.identity) === Number(message.created_by),
                            )?.role === UserRoleName.CareAdvisor
                          ? currentCase?.care_advisor
                          : undefined
                      }
                      messageIndex={index}
                      handleMediaLoad={handleMediaLoad}
                      conversationState={isArchivedConversation ? 'closed' : conversation?.state}
                      direction={Number(message.created_by) === Number(identity) ? 'outgoing' : 'incoming'}
                      messageDetails={{
                        id: message.sid,
                      }}
                      participants={conversation?.participants}
                      providerCredentials={currentCase?.provider?.provider_profile?.credentials}
                      message={message}
                      editMessage={editMessage}
                      deleteMessage={deleteMessage}
                      isVCRoom={isVideoConversation}
                      listInnerRef={listInnerRef}
                      scrollTothis={scrollTothis}
                      setEditMode={setEditMode}
                      isInEditMode={isInEditMode}
                      setIsInEditMode={setIsInEditMode}
                      currentCaseId={currentCase?.id}
                      isLastMessage={index === messages.length - 1}
                      hoveredMessageId={hoveredMessageId}
                      setHoveredMessageId={setHoveredMessageId}
                    />
                  ))}
                <li>
                  {conversation?.participants?.filter((p) => p.role === UserRoleName.Patient) &&
                    conversation?.participants.length === 1 && (
                      <div className={styles.systemMessage}>
                        <div className={styles.systemPopupMessageContainer}>
                          <div className={styles.popupMessage}>
                            <p className="m-0 m-4">A Care Advisor will reach out to you soon</p>
                          </div>
                        </div>
                      </div>
                    )}
                </li>
              </ul>
            </div>

            {!editMode &&
              !isArchivedConversation &&
              conversation?.participants?.find((me) => myEffectiveIdentities.includes(Number(me.identity)))?.status ===
                'active' &&
              conversation?.participants?.some(
                (p) => !myEffectiveIdentities.includes(Number(p.identity)) && p.status === 'active',
              ) &&
              (conversation?.state === 'active' ||
              (conversation?.state === 'inactive' &&
                (userInfo.role.name === UserRoleName.Patient || userInfo.role.name === UserRoleName.CareAdvisor) &&
                conversationType === `${UserRoleName.Patient}_${UserRoleName.CareAdvisor}`) ||
              ((userInfo.role.name === UserRoleName.Provider || userInfo.role.name === UserRoleName.CareAdvisor) &&
                conversationType === `${UserRoleName.Provider}_${UserRoleName.CareAdvisor}` &&
                conversation?.state !== 'closed') ? (
                <div className={styles.chatFooter}>
                  <CaseChatFooter
                    conversationSid={conversation?.sid}
                    sendMessage={sendMessage}
                    choosenFiles={choosenFiles}
                    setChoosenFiles={setChoosenFiles}
                    setDisplayProgressBar={setDisplayProgressBar}
                    displayProgressBar={displayProgressBar}
                    setProgress={setProgress}
                    conversationType={conversationType}
                    caseType={currentCase?.type || ''}
                    twilioConversation={twilioConversation}
                    getParticipantName={getParticipantName}
                  />
                </div>
              ) : (
                !editMode &&
                conversation?.state === 'closed' && (
                  <div className={`${styles.chatFooter} ${styles.messagesFooter}`}>
                    <span>This conversation is now inactive.</span>
                  </div>
                )
              ))}
            {!editMode && isArchivedConversation && (
              <div className={`${styles.chatFooter} ${styles.messagesFooter}`}>
                <span>This is an archived conversation and is read-only.</span>
              </div>
            )}
          </>
        )}
      </div>
      {addParticipantModalOpen && (
        <AddParticipantModal setOpenModal={setAddParticipantModalOpen} conversationSid={conversation?.sid ?? ''} />
      )}
      {isMobile && isMobileOptionsModalOpen && (
        <CaseMobileOptionsModal
          setOpenModal={setIsMobileOptionsModalOpen}
          conversation={conversation}
          isProfileCard={isProfileCard}
          leaveModalOpen={leaveModalOpen}
          setLeaveModalOpen={setLeaveModalOpen}
          query={query}
          setQuery={setQuery}
          setSearch={setSearch}
          setLoading={setLoading}
        />
      )}
      {profileCardModal && (
        <PatientProfileCardModal
          patientIdentity={participant}
          isOpen={profileCardModal}
          toggleModal={setProfileCardModal}
        />
      )}
      {providerProfileCardModal && (
        <ProviderProfileCardModal
          isOpen={providerProfileCardModal}
          toggleModal={setProviderProfileCardModal}
          providerIdentity={participant}
          patientCurrentStateValue={patientCurrentState}
        />
      )}
      {collectorProfileCardModal && (
        <CollectorProfileCardModal
          isOpen={collectorProfileCardModal}
          toggleModal={setCollectorProfileCardModal}
          collectorIdentity={participant}
        />
      )}
      {caProfileCardModal && (
        <CareAdvisorProfileCardModal
          isOpen={caProfileCardModal}
          toggleModal={setCAProfileCardModal}
          careAdvisorIdentity={participant}
        />
      )}
    </>
  );
};

CaseMessageBody.defaultProps = {
  isVideoConversation: false,
  isProfileCard: false,
};

export default CaseMessageBody;
